import React from 'react';
import PropTypes from 'prop-types';
import { EM_DASH, formatNumber } from './queueMonitorFormat';

/**
 * WowCard
 * "Week over week" mini-card. v1 source: extendedStats.wow_daily (executions +
 * failures + failure rate per week, rate_delta signed/colored). v2 (contract 7.4)
 * adds the optional wowHourly prop (extendedStats.wow_hourly, job_metrics
 * attempt-grain): PREFERRED when present AND both week rates are non-null
 * (fully covered); otherwise the card falls back to wow_daily exactly as v1.
 * wow_hourly.coverage_note renders muted whenever present (it is non-null
 * exactly when a week is not fully covered, i.e. when the daily fallback is
 * showing). Negative delta (failure rate fell) is GOOD = green; positive = red;
 * null = em dash.
 *
 * Renders nothing when neither source is usable (old backend during deploy skew).
 */

const formatRatePct = (rate) => {
	if (rate === null || rate === undefined || !Number.isFinite(rate)) {
		return EM_DASH;
	}
	return `${(rate * 100).toFixed(2)}%`;
};

const formatDeltaPct = (delta) => {
	if (delta === null || delta === undefined || !Number.isFinite(delta)) {
		return EM_DASH;
	}
	return `${delta > 0 ? '+' : ''}${(delta * 100).toFixed(2)}%`;
};

const deltaColorClass = (delta) => {
	if (delta === null || delta === undefined || !Number.isFinite(delta) || delta === 0) {
		return 'text-muted';
	}
	return delta < 0 ? 'text-success' : 'text-danger';
};

const WeekRow = ({ label, week, valueKey }) => (
	<tr>
		<td>
			<span className="wow-week-label">{label}</span>
			{week && week.from && week.to && (
				<span className="text-muted small ms-2">{`${week.from} to ${week.to}`}</span>
			)}
		</td>
		<td className="text-end">{formatNumber(week ? week[valueKey] : null)}</td>
		<td className="text-end">{formatNumber(week ? week.failures : null)}</td>
		<td className="text-end">{formatRatePct(week ? week.rate : null)}</td>
	</tr>
);

WeekRow.propTypes = {
	label: PropTypes.string.isRequired,
	week: PropTypes.object,
	valueKey: PropTypes.string,
};

WeekRow.defaultProps = {
	week: null,
	valueKey: 'executions',
};

const WowCard = ({ wow, wowHourly }) => {
	// Prefer wow_hourly ONLY when present and both week rates are non-null
	// (v2 contract 7.4: a not-fully-covered hourly block must never displace
	// the daily card).
	const hourlyUsable = !!(
		wowHourly &&
		wowHourly.this_week &&
		wowHourly.last_week &&
		wowHourly.this_week.rate !== null &&
		wowHourly.this_week.rate !== undefined &&
		wowHourly.last_week.rate !== null &&
		wowHourly.last_week.rate !== undefined
	);

	const active = hourlyUsable ? wowHourly : wow;
	// coverage_note is rendered muted whenever present, regardless of which
	// source is displayed (it explains why the hourly block is not in use).
	const coverageNote =
		wowHourly && wowHourly.coverage_note ? wowHourly.coverage_note : null;

	if (!active) {
		return null;
	}

	const valueKey = hourlyUsable ? 'attempts' : 'executions';
	const valueHeader = hourlyUsable ? 'Attempts' : 'Executions';
	const caption = hourlyUsable
		? 'hourly grain, attempt-level (job_metrics)'
		: 'daily grain, parent jobs only';

	return (
		<div className="stat-card wow-card">
			<div className="stat-card-title">Week over week</div>
			<div className="table-responsive">
				<table className="table table-sm mb-1 wow-table">
					<thead className="table-light">
						<tr>
							<th />
							<th className="text-end">{valueHeader}</th>
							<th className="text-end">Failures</th>
							<th className="text-end">Rate</th>
						</tr>
					</thead>
					<tbody>
						<WeekRow
							label="This week"
							week={active.this_week}
							valueKey={valueKey}
						/>
						<WeekRow
							label="Last week"
							week={active.last_week}
							valueKey={valueKey}
						/>
					</tbody>
				</table>
			</div>
			<div className="wow-delta">
				Failure rate delta:{' '}
				<span className={`wow-delta-value fw-bold ${deltaColorClass(active.rate_delta)}`}>
					{formatDeltaPct(active.rate_delta)}
				</span>
			</div>
			<div className="text-muted small wow-caption">
				{caption}
				{active.basis ? ` (${active.basis})` : ''}
			</div>
			{coverageNote && (
				<div className="text-muted small wow-coverage-note">{coverageNote}</div>
			)}
		</div>
	);
};

WowCard.propTypes = {
	wow: PropTypes.object,
	wowHourly: PropTypes.object,
};

WowCard.defaultProps = {
	wow: null,
	wowHourly: null,
};

export default WowCard;
